/**
 * @file kraService.ts
 * @brief KRA/KPI API service for performance management
 */

import apiClient, { unwrapResponse } from './apiClient';
import { API_ENDPOINTS } from './endpoints';
import type { ApiResponse } from '@/types';

export interface KRA {
  id: string;
  userId: string;
  title: string;
  description?: string;
  startDate: string;
  endDate: string;
  weight: number;
  progress: number;
  status: 'on_track' | 'at_risk' | 'behind';
  createdAt?: string;
  updatedAt?: string;
}

export interface KPI {
  id: string;
  kraId: string;
  title: string;
  current: number;
  target: number;
  unit: string;
  frequency: string;
  createdAt?: string;
  updatedAt?: string;
}

function mapRawToKRA(raw: Record<string, unknown>): KRA {
  return {
    id: String(raw.id ?? ''),
    userId: String(raw.userId ?? ''),
    title: String(raw.title ?? ''),
    description: raw.description != null ? String(raw.description) : undefined,
    startDate: String(raw.startDate ?? ''),
    endDate: String(raw.endDate ?? ''),
    weight: Number(raw.weight ?? 0),
    progress: Number(raw.progress ?? 0),
    status: (raw.status as KRA['status']) ?? 'on_track',
    createdAt: raw.createdAt != null ? String(raw.createdAt) : undefined,
    updatedAt: raw.updatedAt != null ? String(raw.updatedAt) : undefined,
  };
}

function mapRawToKPI(raw: Record<string, unknown>): KPI {
  return {
    id: String(raw.id ?? ''),
    kraId: String(raw.kraId ?? ''),
    title: String(raw.title ?? ''),
    current: Number(raw.current ?? 0),
    target: Number(raw.target ?? 0),
    unit: String(raw.unit ?? ''),
    frequency: String(raw.frequency ?? ''),
    createdAt: raw.createdAt != null ? String(raw.createdAt) : undefined,
    updatedAt: raw.updatedAt != null ? String(raw.updatedAt) : undefined,
  };
}

export const kraKpiService = {
  /**
   * Get all KRAs, optionally filtered by userId
   */
  async getKRAs(userId?: string): Promise<KRA[]> {
    const url = userId 
      ? `${API_ENDPOINTS.KRAS.LIST}?userId=${userId}`
      : API_ENDPOINTS.KRAS.LIST;
    const response = await apiClient.get(url);
    const data = unwrapResponse<Record<string, unknown>[]>(response);
    return Array.isArray(data) ? data.map(mapRawToKRA) : [];
  },

  /**
   * Get a single KRA by ID
   */
  async getKRA(kraId: string): Promise<KRA | null> {
    try {
      const response = await apiClient.get(API_ENDPOINTS.KRAS.GET(kraId));
      const data = unwrapResponse<Record<string, unknown>>(response);
      return data ? mapRawToKRA(data) : null;
    } catch {
      return null;
    }
  },

  /**
   * Create a new KRA
   */
  async createKRA(kraData: Omit<KRA, 'id' | 'createdAt' | 'updatedAt'>): Promise<ApiResponse<KRA>> {
    const response = await apiClient.post(API_ENDPOINTS.KRAS.CREATE, kraData);
    return response.data;
  },

  /**
   * Update an existing KRA
   */
  async updateKRA(kraId: string, kraData: Partial<KRA>): Promise<ApiResponse<KRA>> {
    const response = await apiClient.put(API_ENDPOINTS.KRAS.UPDATE(kraId), kraData);
    return response.data;
  },

  /**
   * Delete a KRA
   */
  async deleteKRA(kraId: string): Promise<ApiResponse<void>> {
    const response = await apiClient.delete(API_ENDPOINTS.KRAS.DELETE(kraId));
    return response.data;
  },

  /**
   * Get all KPIs for a specific KRA
   */
  async getKPIsForKRA(kraId: string): Promise<KPI[]> {
    const response = await apiClient.get(API_ENDPOINTS.KRAS.KPIS(kraId));
    const data = unwrapResponse<Record<string, unknown>[]>(response);
    return Array.isArray(data) ? data.map(mapRawToKPI) : [];
  },

  /**
   * Create a new KPI for a KRA
   */
  async createKPI(kraId: string, kpiData: Omit<KPI, 'id' | 'kraId' | 'createdAt' | 'updatedAt'>): Promise<ApiResponse<KPI>> {
    const response = await apiClient.post(API_ENDPOINTS.KRAS.CREATE_KPI(kraId), kpiData);
    return response.data;
  },

  /**
   * Update a KPI
   */
  async updateKPI(kpiId: string, kpiData: Partial<KPI>): Promise<ApiResponse<KPI>> {
    const response = await apiClient.put(API_ENDPOINTS.KPIS.UPDATE(kpiId), kpiData);
    return response.data;
  },

  /**
   * Delete a KPI
   */
  async deleteKPI(kpiId: string): Promise<ApiResponse<void>> {
    const response = await apiClient.delete(API_ENDPOINTS.KPIS.DELETE(kpiId));
    return response.data;
  },

  /**
   * Get all KPIs for a user (fetches all their KRAs and aggregates KPIs)
   */
  async getKPIsForUser(userId: string): Promise<KPI[]> {
    const kras = await this.getKRAs(userId);
    const kpisPromises = kras.map(kra => this.getKPIsForKRA(kra.id));
    const kpisArrays = await Promise.all(kpisPromises);
    return kpisArrays.flat();
  },
};

export default kraKpiService;
